import { DEFAULT_WRITE_MODEL, WRITE_MODELS, UNLIMITED_EXPERIMENT_VARIATIONS } from "../../../../src/shared/constants.js";
import { resolveWriteModelFallback } from "../../../../src/shared/writeModelFallbacks.js";
import { resolveRetiredDraftMode } from "../../../../src/shared/retiredDirectWriteModels.js";
import { resolveLegacyVoiceProseMode } from "../../../../src/shared/legacyVoiceRetirement.js";

export const BENCHMARK_WRITE_MODEL = "muse-2-alpha";
export const BENCHMARK_DRAFT_MODEL = "MUSE_2_ALPHA";
export const BENCHMARK_MODEL_LABEL = "Muse 2 Alpha";

// Phase 1 standalone turtle candidates were removed when Phase 2 launched.
// Saved standalone choices always fall back to ordinary Write; entering the
// comparison requires an explicit Phase 2 selection.
function resolveStandaloneWriteModel(model) {
  const resolved = resolveWriteModelFallback(model);
  return /^muse2(?:Leonardo|Raphael|Donatello|Michelangelo)$/i.test(resolved) ? DEFAULT_WRITE_MODEL : resolved;
}

export function isStandaloneAlphaDraftModel(model) {
  return /^MUSE2_(?:LEONARDO|RAPHAEL|DONATELLO|MICHELANGELO|ALPHA_[ABCD])$/.test(model);
}

// Keep the picker and generation paths on the same account-specific fallback.
export function getWriteModelSelectionOptions({ subscription, isInUnlimitedExperiment, unlimitedExperimentVariation }) {
  return {
    allowStandaloneAlpha: false,
    allowComparisonAlpha: !!subscription?.hasAccessToMuse2AlphaPhase2,
    fallbackModel: isInUnlimitedExperiment && unlimitedExperimentVariation === UNLIMITED_EXPERIMENT_VARIATIONS.UNLIMITED_DEFAULT
      ? "basic"
      : DEFAULT_WRITE_MODEL,
  };
}

// Prose Mode is an account preference. Old project selections are only a
// fallback for accounts without a saved global mode. A saved comparison choice
// requires enrollment too; it must not expose Alpha or send comparison requests
// when the account no longer has access.
export function getSelectedWriteModel(globalModel, projectModel, { allowComparisonAlpha = false, fallbackModel = DEFAULT_WRITE_MODEL } = {}) {
  const resolveModel = (model) =>
    model === BENCHMARK_WRITE_MODEL && !allowComparisonAlpha ? fallbackModel : resolveStandaloneWriteModel(model);
  const resolvedGlobalModel = resolveModel(globalModel);
  if (WRITE_MODELS.some((model) => model.value === resolvedGlobalModel)) return resolvedGlobalModel;
  const resolvedProjectModel = resolveModel(projectModel);
  return WRITE_MODELS.some((model) => model.value === resolvedProjectModel)
    ? resolvedProjectModel
    : resolvedGlobalModel;
}

// Once an account has chosen Draft's mode, old project and scene defaults must
// not override it, including when a standalone choice needs a safe fallback.
export function getSelectedDraftMode({ globalModel, projectModel, sceneModel, proseModes, fallbackMode, allowComparisonAlpha = false, legacyVoiceModels = [] }) {
  for (const model of [globalModel, projectModel, sceneModel]) {
    if (model === BENCHMARK_DRAFT_MODEL && !allowComparisonAlpha) return fallbackMode;
    if (isStandaloneAlphaDraftModel(model)) return fallbackMode;
    const mode = proseModes[resolveRetiredDraftMode(resolveLegacyVoiceProseMode(model, legacyVoiceModels))];
    if (mode?.scenesEngine) return mode;
    if (model === globalModel && model) return fallbackMode;
  }
  return undefined;
}

// The picker mode controls the experience; existing engines still provide the
// generation and billing contract underneath it.
export function resolveBenchmarkWriteModel(model) {
  return model === BENCHMARK_WRITE_MODEL ? "excellent" : model;
}

export const BENCHMARK_PICKER_MODEL = {
  type: "main",
  muse2Alpha: true,
  icon: "/assets/model-selection-modal/muse_m.svg",
  label: BENCHMARK_MODEL_LABEL,
  subLabel: "Compare and rate new prose",
  writeModelId: BENCHMARK_WRITE_MODEL,
  proseModelId: BENCHMARK_DRAFT_MODEL,
  description:
    "Help shape the next Muse! Each time you generate using the Muse 2 Alpha Prose Mode, you’ll receive two versions of your results. Read and rate them both, and then use your favorite. Toggle back to any other Prose Mode if you’d like to stop comparing results.\n\nCredits are refunded each time you rate your Muse 2 Alpha generations.",
};
